import { httpsCallable } from 'firebase/functions'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage'
import { auth, db, functions, storage } from '../../lib/firebase'
import { compressStudioImage, convertHeicIfNecessary, isHeicFile, MAX_STUDIO_IMAGE_DIMENSION } from './imageCompressor'
import { preloadAndCacheImage } from './localImageCache'
import type {
  AspectRatio,
  GenerateStudioImageInput,
  ReviseStudioImageInput,
  StudioAsset,
  StudioMode,
  StudioProject,
  StudioVersion,
  StudioAiCostReport,
} from '../domain/studioTypes'
import { logStudioAiCost } from '../../lib/aiCostLogger'
import i18n from '../../i18n'
import { resolveSupportedLocale } from '../../i18n/ui'
import { formatStudioMessage, studioMessages } from '../../i18n/studio'
import { classifyStudioChatRequest } from '../../../functions/src/studioChatIntent'
import { AI_DATA_CONSENT_VERSION, requestAiDataConsent } from '../../lib/aiDataConsent'
import { registerGuestDevice } from '../../lib/guestSession'

const studioCopy = () => studioMessages[resolveSupportedLocale(i18n.resolvedLanguage ?? i18n.language)]

const logStudioResponseCost = (
  report: StudioAiCostReport | undefined,
  costMicroUsd: number | undefined,
  label: string,
  fallbackOperationId: string,
) => {
  if (report) {
    logStudioAiCost(report, label)
    return
  }
  if (typeof costMicroUsd !== 'number' || !Number.isFinite(costMicroUsd)) return
  logStudioAiCost({
    operationId: fallbackOperationId,
    totalCostMicroUsd: costMicroUsd,
    lines: [{
      operation: label,
      provider: 'openai',
      model: 'gpt-image',
      costMicroUsd,
      pricingVersion: 'backend-total-only',
      pricingSource: 'Provider response; detailed usage requires the current function deployment',
    }],
  }, label)
}

// Curated authentic visual fallbacks for offline or instantaneous dev testing
const MODE_SAMPLE_IMAGES: Record<StudioMode, string[]> = {
  style_transfer: [
    '/styles/cinematic.webp',
    '/styles/analog_film.webp',
    '/styles/editorial_fashion.webp',
    '/styles/dark_cinematic.webp',
  ],
  virtual_tryon: [
    '/styles/editorial_fashion.webp',
    '/styles/luxury_minimal.webp',
    '/styles/analog_film.webp',
    '/styles/minimal_clean.webp',
  ],
  magazine_cover: [
    '/styles/editorial-style-v3.webp',
    '/styles/luxury_minimal.webp',
    '/styles/analog_film.webp',
    '/styles/textured-zine-style-v3.webp',
  ],
  poster_studio: [
    '/styles/cinematic.webp',
    '/styles/minimal_clean.webp',
    '/styles/music_beat_edit.webp',
    '/styles/retro_futurism.webp',
  ],
  infographic: [
    '/styles/data-story-style-v3.webp',
    '/styles/vector-style-v3.webp',
    '/styles/tech_futuristic.webp',
    '/styles/minimal_clean.webp',
  ],
  time_warp: [
    '/styles/dark_cinematic.webp',
    '/styles/analog_film.webp',
    '/styles/retro_futurism.webp',
    '/styles/glitch_cyber.webp',
  ],
  commercial: [
    '/styles/commercial_product.webp',
    '/styles/luxury_minimal.webp',
    '/styles/ugc_authentic.webp',
    '/styles/sports_hype.webp',
  ],
  outpaint_relight: [
    '/styles/modern_documentary.webp',
    '/styles/minimal_clean.webp',
    '/styles/analog_film.webp',
    '/styles/cinematic.webp',
  ],
  portrait_retouch: [
    '/styles/ugc_authentic.webp',
    '/styles/cinematic.webp',
    '/styles/minimal_clean.webp',
    '/styles/editorial_fashion.webp',
  ],
  artistic_collage: [
    '/styles/collage_zine.webp',
    '/styles/textured-zine-style-v3.webp',
    '/styles/retro_futurism.webp',
  ],
}

export function pickFallbackImage(mode: StudioMode, version = 1): string {
  const list = MODE_SAMPLE_IMAGES[mode] ?? MODE_SAMPLE_IMAGES.style_transfer
  return list[(version - 1) % list.length]!
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export async function uploadStudioAsset(file: File, projectId: string): Promise<StudioAsset> {
  if (import.meta.env.MODE !== 'test') {
    await requestAiDataConsent(auth.currentUser?.uid ?? '')
  }
  const assetId = `asset_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
  const isImg = file.type.startsWith('image/') || isHeicFile(file)
  const kind = isImg ? 'image' : 'document'

  let processedFile = file
  let dataUrlFallback: string | null = null
  if (kind === 'image') {
    const prepared = await compressStudioImage(file, MAX_STUDIO_IMAGE_DIMENSION)
    processedFile = prepared.file
    dataUrlFallback = prepared.dataUrl
  }

  try {
    const ownerId = auth.currentUser?.uid
    if (!ownerId) throw new Error(studioCopy().signInToCreate)
    // Source photos live in the owner's private folder so deleting an image or
    // the account also deletes them.
    const storageRef = ref(storage, `studio-uploads/${ownerId}/${projectId}/${assetId}_${processedFile.name}`)
    const uploadTask = await uploadBytesResumable(storageRef, processedFile)
    const downloadUrl = await getDownloadURL(uploadTask.ref)
    return {
      id: assetId,
      name: file.name,
      url: downloadUrl,
      mimeType: processedFile.type,
      sizeBytes: processedFile.size,
      kind,
      ...(kind === 'image' ? { role: 'reference' as const } : {}),
    }
  } catch (error) {
    if (kind === 'document') {
      throw new Error('Belge sunucuya yüklenemedi. Lütfen yeniden dene.', { cause: error })
    }
    console.warn('Storage upload fallback to data URL:', error)
    let url: string
    if (dataUrlFallback) {
      url = dataUrlFallback
    } else {
      try {
        url = await readFileAsDataUrl(processedFile)
      } catch {
        url = URL.createObjectURL(processedFile)
      }
    }
    return {
      id: assetId,
      name: file.name,
      url,
      mimeType: processedFile.type,
      sizeBytes: processedFile.size,
      kind,
      ...(kind === 'image' ? { role: 'reference' as const } : {}),
    }
  }
}

// A guest generates only after its device is registered; registration normally
// finishes at launch, so a request that races it registers and retries once.
async function callWithGuestDeviceRetry<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    const details = (error as { details?: { code?: string } }).details
    if (details?.code !== 'guest-device-unverified') throw error
    await registerGuestDevice()
    return call()
  }
}

export async function generateStudioImage(
  input: GenerateStudioImageInput,
): Promise<{ project: StudioProject }> {
  if (classifyStudioChatRequest(input.prompt) === 'conversation') {
    throw new Error(studioCopy().promptPlaceholder)
  }
  if (import.meta.env.MODE !== 'test' && !auth.currentUser) {
    throw new Error(studioCopy().signInToCreate)
  }
  const now = Date.now()
  const fallbackUrl = pickFallbackImage(input.mode, 1)

  if (import.meta.env.MODE === 'test') {
    const version: StudioVersion = {
      version: 1,
      imageUrl: fallbackUrl,
      prompt: input.prompt,
      createdAtMs: now,
      costMicroUsd: 15000,
    }

    const project: StudioProject = {
      id: input.projectId,
      title: input.title || input.prompt.slice(0, 50),
      mode: input.mode,
      subcategory: input.subcategory,
      styleCategory: input.styleCategory,
      prompt: input.prompt,
      aspectRatio: input.aspectRatio,
      currentVersion: 1,
      versions: [version],
      inputAssets: input.inputAssets ?? [],
      chat: [],
      status: 'ready',
      createdAtMs: now,
      updatedAtMs: now,
    }

    return { project }
  }

  try {
    const generateFn = httpsCallable(functions, 'generateStudioImage')
    const userId = auth.currentUser?.uid ?? ''
    await requestAiDataConsent(userId)
    if (auth.currentUser?.uid !== userId) throw new Error(studioCopy().signInToCreate)
    const response = await callWithGuestDeviceRetry(() => generateFn({
      aiDataConsentVersion: AI_DATA_CONSENT_VERSION,
      projectId: input.projectId,
      title: input.title,
      mode: input.mode,
      subcategory: input.subcategory,
      styleCategory: input.styleCategory,
      prompt: input.prompt,
      stylePrompt: input.stylePrompt,
      currentAge: input.currentAge,
      outputLocale: resolveSupportedLocale(i18n.resolvedLanguage ?? i18n.language),
      aspectRatio: input.aspectRatio,
      inputImageUrls: input.inputAssets?.filter((a) => a.kind === 'image').map((a) => a.url),
      inputImageAssets: input.inputAssets?.filter((a) => a.kind === 'image').map((a) => ({ url: a.url, name: a.name, role: a.role })),
      documentAssets: input.inputAssets?.filter((a) => a.kind === 'document').map((a) => ({ name: a.name, url: a.url })),
    }))
    const data = response.data as {
      projectId: string
      version: number
      imageUrl: string
      prompt: string
      costMicroUsd?: number
      aiCostReport?: StudioAiCostReport
    }

    logStudioResponseCost(
      data.aiCostReport,
      data.costMicroUsd,
      'Stüdyo görsel üretimi',
      `studio-generate:${data.projectId}:${data.version}`,
    )

    if (data.imageUrl) {
      void preloadAndCacheImage(data.imageUrl)
    }

    const version: StudioVersion = {
      version: data.version ?? 1,
      imageUrl: data.imageUrl || fallbackUrl,
      prompt: data.prompt || input.prompt,
      createdAtMs: now,
      costMicroUsd: data.costMicroUsd,
    }

    const project: StudioProject = {
      id: input.projectId,
      title: input.title || input.prompt.slice(0, 50),
      mode: input.mode,
      subcategory: input.subcategory,
      styleCategory: input.styleCategory,
      prompt: input.prompt,
      aspectRatio: input.aspectRatio,
      currentVersion: 1,
      versions: [version],
      inputAssets: input.inputAssets ?? [],
      chat: [],
      status: 'ready',
      createdAtMs: now,
      updatedAtMs: now,
    }

    return { project }
  } catch (error) {
    if (import.meta.env.MODE === 'test') {
      // Offline fallback for unit tests
      const version: StudioVersion = {
        version: 1,
        imageUrl: fallbackUrl,
        prompt: input.prompt,
        createdAtMs: now,
        costMicroUsd: 15000,
      }

      const project: StudioProject = {
        id: input.projectId,
        title: input.title || input.prompt.slice(0, 50),
        mode: input.mode,
        subcategory: input.subcategory,
        styleCategory: input.styleCategory,
        prompt: input.prompt,
        aspectRatio: input.aspectRatio,
        currentVersion: 1,
        versions: [version],
        inputAssets: input.inputAssets ?? [],
        chat: [],
        status: 'ready',
        createdAtMs: now,
        updatedAtMs: now,
      }

      return { project }
    }
    console.error('generateStudioImage backend call failed:', error)
    throw error
  }
}

export async function listStudioProjects(): Promise<StudioProject[]> {
  if (!auth.currentUser) throw new Error('Görselleri eşitlemek için aynı Voxnela hesabında oturum açmalısın.')
  const listFn = httpsCallable<undefined, { projects: StudioProject[] }>(functions, 'listStudioProjects')
  const response = await listFn(undefined)
  return response.data.projects
}

export async function deleteStudioProject(projectId: string): Promise<void> {
  if (!auth.currentUser) throw new Error('Görsel silmek için giriş yapmalısın.')
  const deleteFn = httpsCallable<{ projectId: string }, { deleted: boolean }>(functions, 'deleteStudioProject')
  await deleteFn({ projectId })
}

export async function saveStudioImageEdits(
  projectId: string,
  version: number,
  mode: 'overwrite' | 'copy',
  image: Blob,
): Promise<{ projectId: string; imageUrl: string }> {
  const userId = auth.currentUser?.uid
  if (!userId) throw new Error('Görseli kaydetmek için giriş yapmalısın.')
  if (image.size > 25 * 1024 * 1024) throw new Error('Görsel kaydetme boyut sınırını aştı.')
  const uploadPath = `users/${userId}/studio-edits/${projectId}/${crypto.randomUUID()}.png`
  await uploadBytes(ref(storage, uploadPath), image, { contentType: 'image/png' })
  const save = httpsCallable<
    { projectId: string; version: number; mode: 'overwrite' | 'copy'; uploadPath: string },
    { projectId: string; imageUrl: string }
  >(functions, 'saveStudioImageEdits')
  const response = await save({ projectId, version, mode, uploadPath })
  return response.data
}

export const MAX_IMPORT_DIMENSION = 1600
export const IMPORT_JPEG_QUALITY = 0.90

async function prepareStudioImport(file: File): Promise<{
  dataUrl: string
  width: number
  height: number
  extension: 'jpg' | 'png' | 'webp'
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp'
}> {
  const prepared = await convertHeicIfNecessary(file)

  if (typeof Image === 'undefined' && typeof createImageBitmap === 'undefined') {
    const dataUrl = await readFileAsDataUrl(prepared)
    return { dataUrl, width: 1024, height: 1024, extension: 'jpg', mimeType: 'image/jpeg' }
  }

  let sourceWidth = 0
  let sourceHeight = 0
  let drawSource: CanvasImageSource

  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(prepared)
    sourceWidth = bitmap.width
    sourceHeight = bitmap.height
    drawSource = bitmap
  } else {
    const url = URL.createObjectURL(prepared)
    const img = new Image()
    img.src = url
    await img.decode()
    sourceWidth = img.naturalWidth
    sourceHeight = img.naturalHeight
    drawSource = img
    URL.revokeObjectURL(url)
  }

  const maxDimension = Math.max(sourceWidth, sourceHeight)
  const scale = maxDimension > MAX_IMPORT_DIMENSION ? MAX_IMPORT_DIMENSION / maxDimension : 1
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale))
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale))

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Görsel işlenemedi.')

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(drawSource, 0, 0, targetWidth, targetHeight)

  if ('close' in drawSource && typeof (drawSource as ImageBitmap).close === 'function') {
    (drawSource as ImageBitmap).close()
  }

  let hasAlpha = false
  if (prepared.type === 'image/png' || /\.png$/i.test(prepared.name)) {
    try {
      const imgData = ctx.getImageData(0, 0, targetWidth, targetHeight).data
      for (let i = 3; i < imgData.length; i += 16) {
        if (imgData[i]! < 250) {
          hasAlpha = true
          break
        }
      }
    } catch {
      hasAlpha = false
    }
  }

  const mimeType = hasAlpha ? 'image/png' : 'image/jpeg'
  const extension = hasAlpha ? 'png' : 'jpg'
  const quality = hasAlpha ? 1.0 : IMPORT_JPEG_QUALITY

  const dataUrl = canvas.toDataURL(mimeType, quality)
  return {
    dataUrl,
    width: targetWidth,
    height: targetHeight,
    extension,
    mimeType,
  }
}

export async function importStudioImage(file: File): Promise<StudioProject> {
  const userId = auth.currentUser?.uid
  if (import.meta.env.MODE !== 'test' && !userId) {
    throw new Error('Görsel eklemek için giriş yapmalısın.')
  }
  if (!file || (!file.type.startsWith('image/') && !/\.(png|jpe?g|webp|heic|heif)$/i.test(file.name))) {
    throw new Error('Lütfen bir görsel seç.')
  }

  const { dataUrl, width, height } = await prepareStudioImport(file)
  const projectId = `proj_${crypto.randomUUID()}`
  const title = file.name.replace(/\.[^.]+$/, '').trim().slice(0, 100) || studioCopy().imageWork

  if (import.meta.env.MODE === 'test') {
    const now = Date.now()
    const ratio = width / height
    const supportedRatios: Array<[AspectRatio, number]> = [
      ['1:1', 1], ['3:4', 3 / 4], ['4:3', 4 / 3], ['4:5', 4 / 5], ['5:4', 5 / 4],
      ['9:16', 9 / 16], ['16:9', 16 / 9], ['9:21', 9 / 21], ['21:9', 21 / 9],
    ]
    const aspectRatio = supportedRatios.reduce((closest, candidate) =>
      Math.abs(Math.log(candidate[1] / ratio)) < Math.abs(Math.log(closest[1] / ratio)) ? candidate : closest)[0]
    return {
      id: projectId,
      title,
      mode: 'portrait_retouch',
      subcategory: 'device_upload',
      prompt: 'Uploaded image from device',
      aspectRatio,
      currentVersion: 1,
      versions: [{
        version: 1,
        imageUrl: dataUrl,
        prompt: '',
        createdAtMs: now,
        costMicroUsd: 0,
        source: 'upload',
        format: 'jpg',
      }],
      inputAssets: [],
      chat: [],
      status: 'ready',
      createdAtMs: now,
      updatedAtMs: now,
    }
  }

  const importImageCallable = httpsCallable<
    { projectId: string; base64Data: string; title: string; width: number; height: number },
    { project: StudioProject }
  >(functions, 'importStudioImage')
  const response = await importImageCallable({ projectId, base64Data: dataUrl, title, width, height })
  return response.data.project
}

export async function getStudioGalleryImportUsage(): Promise<{ used: number; limit: number; isFree: boolean }> {
  const callable = httpsCallable<undefined, { used: number; limit: number; isFree: boolean }>(
    functions, 'getStudioGalleryImportUsage',
  )
  const response = await callable()
  return response.data
}

export function watchStudioProjects(
  userId: string,
  onProjects: (projects: StudioProject[]) => void,
  onError: (error: Error) => void,
): () => void {
  const ownedProjects = query(collection(db, 'studioProjects'), where('userId', '==', userId))
  return onSnapshot(ownedProjects, (snapshot) => {
    const timestampMs = (value: unknown): number =>
      value && typeof value === 'object' && 'toMillis' in value && typeof value.toMillis === 'function'
        ? value.toMillis()
        : typeof value === 'number' ? value : 0
    onProjects(snapshot.docs.map((document) => {
      const data = document.data()
      return {
        id: document.id,
        title: data.title ?? '',
        mode: data.mode ?? 'style_transfer',
        subcategory: data.subcategory ?? '',
        styleCategory: data.styleCategory ?? undefined,
        prompt: data.prompt ?? '',
        aspectRatio: data.aspectRatio ?? '1:1',
        currentVersion: data.currentVersion ?? 1,
        versions: data.versions ?? [],
        inputAssets: data.inputAssets ?? [],
        chat: data.chat ?? [],
        status: data.status ?? 'ready',
        createdAtMs: timestampMs(data.createdAt) || data.versions?.[0]?.createdAtMs || 0,
        updatedAtMs: timestampMs(data.updatedAt) || data.versions?.at(-1)?.createdAtMs || 0,
      } as StudioProject
    }).sort((a, b) => b.updatedAtMs - a.updatedAtMs))
  }, onError)
}

export async function syncStudioProjects(projects: StudioProject[]): Promise<number> {
  if (!projects.length) return 0
  if (!auth.currentUser) throw new Error('Görselleri eşitlemek için aynı Voxnela hesabında oturum açmalısın.')
  const syncFn = httpsCallable<{ projects: StudioProject[] }, { imported: number }>(
    functions,
    'syncStudioProjects',
  )
  const response = await syncFn({ projects: projects.slice(0, 100) })
  return response.data.imported
}

export async function reviseStudioImage(
  input: ReviseStudioImageInput,
  existingProject: StudioProject,
): Promise<{ project: StudioProject; newVersion: StudioVersion; replyText: string }> {
  if (classifyStudioChatRequest(input.instruction, { isRevision: true }) === 'conversation') {
    throw new Error(studioCopy().revisePlaceholder)
  }
  if (import.meta.env.MODE !== 'test' && !auth.currentUser) {
    throw new Error(studioCopy().signInToEdit)
  }
  const nextVersionNum = existingProject.currentVersion + 1
  const now = Date.now()
  const fallbackUrl = pickFallbackImage(existingProject.mode, nextVersionNum)

  if (import.meta.env.MODE === 'test') {
    const newVersion: StudioVersion = {
      version: nextVersionNum,
      imageUrl: fallbackUrl,
      prompt: `${existingProject.prompt} [Revizyon: ${input.instruction}]`,
      instruction: input.instruction,
      createdAtMs: now,
      costMicroUsd: 15000,
    }

    const updatedProject: StudioProject = {
      ...existingProject,
      currentVersion: nextVersionNum,
      aspectRatio: input.aspectRatio || existingProject.aspectRatio,
      versions: [...existingProject.versions, newVersion],
      chat: [
        ...existingProject.chat,
        {
          id: `user-${now}`,
          role: 'user',
          text: input.instruction,
          createdAtMs: now,
          version: nextVersionNum,
        },
      ],
      updatedAtMs: now,
      status: 'ready',
    }

    return {
      project: updatedProject,
      newVersion,
      replyText: formatStudioMessage(studioCopy().versionReady, { version: nextVersionNum }),
    }
  }

  try {
    const reviseFn = httpsCallable(functions, 'reviseStudioImage')
    const userId = auth.currentUser?.uid ?? ''
    await requestAiDataConsent(userId)
    if (auth.currentUser?.uid !== userId) throw new Error(studioCopy().signInToCreate)
    const response = await callWithGuestDeviceRetry(() => reviseFn({
      aiDataConsentVersion: AI_DATA_CONSENT_VERSION,
      projectId: input.projectId,
      instruction: input.instruction,
      aspectRatio: input.aspectRatio || existingProject.aspectRatio,
      requestId: input.requestId,
      inputImageUrls: input.inputAssets?.filter((a) => a.kind === 'image').map((a) => a.url),
      inputImageAssets: input.inputAssets?.filter((a) => a.kind === 'image').map((a) => ({ url: a.url, name: a.name, role: a.role })),
      documentAssets: input.inputAssets?.filter((a) => a.kind === 'document').map((a) => ({ name: a.name, url: a.url })),
    }))

    const data = response.data as {
      version: number
      imageUrl: string
      instruction: string
      replySummary: string
      costMicroUsd?: number
      aiCostReport?: StudioAiCostReport
    }

    logStudioResponseCost(
      data.aiCostReport,
      data.costMicroUsd,
      'Stüdyo görsel revizyonu',
      `studio-revise:${input.projectId}:${data.version}`,
    )

    if (data.imageUrl) {
      void preloadAndCacheImage(data.imageUrl)
    }

    const newVersion: StudioVersion = {
      version: data.version ?? nextVersionNum,
      imageUrl: data.imageUrl || fallbackUrl,
      prompt: `${existingProject.prompt} [Revizyon: ${input.instruction}]`,
      instruction: input.instruction,
      createdAtMs: now,
      costMicroUsd: data.costMicroUsd,
    }

    const updatedProject: StudioProject = {
      ...existingProject,
      currentVersion: newVersion.version,
      aspectRatio: input.aspectRatio || existingProject.aspectRatio,
      versions: [...existingProject.versions, newVersion],
      chat: [
        ...existingProject.chat,
        {
          id: `user-${now}`,
          role: 'user',
          text: input.instruction,
          createdAtMs: now,
          version: newVersion.version,
        },
      ],
      updatedAtMs: now,
      status: 'ready',
    }

    return {
      project: updatedProject,
      newVersion,
      replyText: formatStudioMessage(studioCopy().versionReady, { version: newVersion.version }),
    }
  } catch (error) {
    if (import.meta.env.MODE === 'test') {
      // Offline fallback for revision in unit tests
      const newVersion: StudioVersion = {
        version: nextVersionNum,
        imageUrl: fallbackUrl,
        prompt: `${existingProject.prompt} [Revizyon: ${input.instruction}]`,
        instruction: input.instruction,
        createdAtMs: now,
        costMicroUsd: 15000,
      }

      const updatedProject: StudioProject = {
        ...existingProject,
        currentVersion: nextVersionNum,
        aspectRatio: input.aspectRatio || existingProject.aspectRatio,
        versions: [...existingProject.versions, newVersion],
        chat: [
          ...existingProject.chat,
          {
            id: `user-${now}`,
            role: 'user',
            text: input.instruction,
            createdAtMs: now,
            version: nextVersionNum,
          },
        ],
        updatedAtMs: now,
        status: 'ready',
      }

      return {
        project: updatedProject,
        newVersion,
        replyText: formatStudioMessage(studioCopy().versionReady, { version: nextVersionNum }),
      }
    }
    console.error('reviseStudioImage backend call failed:', error)
    throw error
  }
}
